import React from "react";
import { withStyles } from "@material-ui/core/styles";
import styles from "./styles";
import { RouteMap } from "../Map";
import MapHeader from "../Map/components/MapHeader";
import { Grid } from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import StatusBar from '@dataGrid/components/StatusBar';
import {
  getDriverPickupIds,
  getSelectedDriverRouteBasedOnPickup,
  getOptimizeSolutionWithDriver,
  selectedDriverAssignments,
  getSelectedStopsList,
  removePickupIdFromList,
  getAvailablePickupForOptimization,
} from "../utils";
import RouteSolution from "./RouteSolution";
import UnassignPickup from "./UnassignPickup";
import { uniqBy } from "lodash";

class OptimizationSolution extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedPickupId: [],
      selectedDriverAssignmentId: [],
      noFeasibleDriverPickups: this.getNonFeasibleDriverPickups(),
      isAutoZoomEnabled: true
    };
  }

  componentDidMount() {
    this.getSelectedPickupId();
  }

  getSelectedPickupId = () => {
    const optimizeSolutionWithDriver = getOptimizeSolutionWithDriver(
      this.props.driverAssignmentsList
    );
    const selectedPickupId = getDriverPickupIds(optimizeSolutionWithDriver);
    this.handleAssignPickupSelection(selectedPickupId);
  };

  handleAssignPickupSelection = (selectedPickupId) => {
    this.setState({ selectedPickupId });
    const selectedDriverAssignment = getSelectedDriverRouteBasedOnPickup(
      this.props.driverAssignmentsList,
      selectedPickupId
    ); //select/unselect a driver route based on selected pickupId
    this.setSelectedDriverAssignment(selectedDriverAssignment);
    const selectedPickups = getSelectedStopsList({
      selectedDriverAssignment,
      selectedPickupId,
      routesList: this.props.routesList,
    });
    this.props.handleAssignPickupSelection(selectedPickups);
  };

  setSelectedDriverAssignment = (selectedDriverAssignment) => {
    const selectedDriverAssignmentId = selectedDriverAssignment.map(
      (driver) => driver.driverId
    );
    this.setState({ selectedDriverAssignmentId });
  };

  onDriverSelection = (e, { driverId, groupIds }) => {
    let selectedDriverAssignmentId = [];
    if (e.target.checked) {
      selectedDriverAssignmentId = [
        ...this.state.selectedDriverAssignmentId,
        driverId,
      ];
    } else {
      selectedDriverAssignmentId = this.state.selectedDriverAssignmentId.filter(
        (driverAssignment) => driverAssignment !== driverId
      );
    }
    this.setState({ selectedDriverAssignmentId });
    this.onPickupSelection(e, groupIds);
  };

  onPickupSelection = (e, groupIds) => {
    let selectedPickupId = [];
    if (e.target.checked) {
      selectedPickupId = [...this.state.selectedPickupId, ...groupIds];
    } else {
      selectedPickupId = this.state.selectedPickupId.filter(
        (groupId) => !groupIds.includes(groupId.toLowerCase())
      );
    }
    this.handleAssignPickupSelection(selectedPickupId);
  };

  updateDriverToUnassignedPickups = (noFeasibleDriverPickups) => {
    this.setState({
      noFeasibleDriverPickups,
    });
  };

  onUnassignPickupSelection = (pickupData) => {
    const unassignPickupSelected = this.props.unassignPickupSelected;
    const index = unassignPickupSelected.findIndex(
      (obj) =>
        obj.pickupId?.toLowerCase() === pickupData.pickupId?.toLowerCase()
    );
    if (index === -1) {
      unassignPickupSelected.push(pickupData);
    } else {
      unassignPickupSelected[index] = pickupData;
    }
    this.props.handleUnassignPickupSelection(
      uniqBy(
        this.props.unassignPickupSelected,
        "pickupId"
      )
    );
  };

  onUnassignPickupDeselection = (pickupId) => {
    const unassignedPickups = removePickupIdFromList(
      this.props.unassignPickupSelected,
      pickupId
    );
    this.props.handleUnassignPickupSelection(
      unassignedPickups
    );
  };

  constructStatusBar = () => {
    const totalPickups = getAvailablePickupForOptimization(
      this.props.driverAssignmentsList
    );
    const totalUnassignedPickups = this.getNonFeasibleDriverIds()?.length ?? 0;
    return [
      {
        label: totalPickups + " Total Pickups",
      },
      {
        label:
          this.state.selectedPickupId.length +
          this.props.unassignPickupSelected.length +
          " Selected Pickups",
      },
      {
        label: totalUnassignedPickups + " Unassign Pickups",
      },
    ];
  };

  getNonFeasibleDriverIds = () => {
    return this.props.driverAssignmentsList
      .map((driverAssignment) => !driverAssignment.driverId && driverAssignment)
      .filter((v) => v);
  };

  getSelectedUnassignPickupIds = () => {
    return this.props.unassignPickupSelected.map((pickup) => pickup.pickupId);
  };

  getNonFeasibleDriverPickups = () => {
    return this.props.driverAssignmentsList.filter((d) => !d.driverId);
  };

  setRouteAnimation = (value) => {
    this.setState({
      isAutoZoomEnabled: value,
    });
  };

  render() {
    const { classes, driverAssignmentsList, unassignPickupDropdownError } =
      this.props;
    const {
      selectedDriverAssignmentId,
      selectedPickupId,
      noFeasibleDriverPickups,
      isAutoZoomEnabled,
    } = this.state;

    const driverAssignments = getOptimizeSolutionWithDriver(
      driverAssignmentsList
    );
    const noFeasibleDriver = this.getNonFeasibleDriverIds();
    const selectedUnassignPickupIds = this.getSelectedUnassignPickupIds();
    return (
      <div
        className={
          noFeasibleDriver?.length > 0
            ? classes.containerWithoutError
            : classes.container
        }
      >
        {noFeasibleDriver?.length > 0 && (
          <div className={classes.errorBar}>
            <ErrorOutlineIcon />
            <span className={classes.errorBarText}>
              There was no feasible driver available for{" "}
              {noFeasibleDriver.length} unassigned pickup, you can assign
              driver(s) manually.
            </span>
          </div>
        )}
        <Grid container className={classes.container}>
          <Grid item xs={4} className={classes.topGridContainer}>
            <RouteSolution
              selectedDriverAssignmentId={selectedDriverAssignmentId}
              driverAssignments={driverAssignments}
              selectedPickupId={selectedPickupId}
              onDriverSelection={this.onDriverSelection}
              onPickupSelection={this.onPickupSelection}
            />
            <UnassignPickup
              noFeasibleDriverPickups={noFeasibleDriverPickups}
              onUnassignPickupSelection={this.onUnassignPickupSelection}
              selectedUnassignPickupIds={selectedUnassignPickupIds}
              onUnassignPickupDeselection={this.onUnassignPickupDeselection}
              updateDriverToUnassignedPickups={
                this.updateDriverToUnassignedPickups
              }
              unassignPickupDropdownError={unassignPickupDropdownError}
            />
          </Grid>
          <Grid item xs={8}>
            <div className={classes.routeMap}>
              <MapHeader label="Route Preview" setRouteAnimation={this.setRouteAnimation}/>
              <RouteMap
                mapId='routeMap'
                isAutoZoomEnabled = {isAutoZoomEnabled}
                selectedRoutes={selectedDriverAssignments(
                  driverAssignments,
                  selectedDriverAssignmentId
                )}
                unassignedPickups={noFeasibleDriverPickups}
                glEventHub={this.props.glEventHub}
                selectedUnassignPickupIds={selectedUnassignPickupIds}
                onUnassignPickupSelection={this.onUnassignPickupSelection}
                onUnassignPickupDeselection={this.onUnassignPickupDeselection}
              />
            </div>
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.statusBarGridItem}>
          <StatusBar data={this.constructStatusBar()} />
        </Grid>
      </div>
    );
  }
}

export default withStyles(styles)(OptimizationSolution);
